import SettingsSaveNav from '@/components/SettingsSaveNav';
import {
  Block,
  BlockTitle,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Popover,
  f7,
} from 'framework7-react';
import { useMemo, useRef } from 'react';
import { useController } from 'react-hook-form';
import { COUNTRY_CODES } from '@restaurant/types';
import { useErrorT, useI18nStore, useT } from '@/i18n';
import { ApiError, resolveErrorText } from '@/api/errors';
import { useContractForm, useListInput } from '@/lib/forms/useContractForm';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import {
  useFranchise,
  useRestaurant,
  useUpdateFranchise,
  useUpdateRestaurant,
} from '../hooks/useRestaurantSetup';
import { useVenueLanguage } from '../hooks/useVenueLanguage';
import { SocialPlatformIcon } from '../components/SocialPlatformIcon';
import {
  EMPTY_PROFILE,
  PROFILE_FIELDS,
  profileBody,
  profileSchema,
  profileValues,
} from '../lib/profile-form';
import { LANGUAGE_CODES } from '../lib/venue-language';

function RestaurantSetupPage() {
  const t = useT();
  const errorMessages = useErrorT();
  const tr = t.restaurantSetup;
  // Location strings live under the order-setup namespace with the map editor
  // itself; reused here so the entry point can move to the business profile
  // without duplicating copy.
  const lp = t.orderSetup.page;
  const locale = useI18nStore((s) => s.locale);
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: restaurant, isLoading } = useRestaurant();
  const update = useUpdateRestaurant();
  const { data: franchise } = useFranchise(restaurant?.franchiseId);
  const updateFranchise = useUpdateFranchise(restaurant?.franchiseId);
  const { language, languageDirty, pickLanguage, followCountryChange } = useVenueLanguage(
    restaurant,
    franchise,
  );
  const hasLocation = restaurant?.latitude != null && restaurant?.longitude != null;

  const countryOptions = useMemo(() => {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    return COUNTRY_CODES.map((code) => ({ code, label: names.of(code) ?? code })).sort((a, b) =>
      a.label.localeCompare(b.label, locale),
    );
  }, [locale]);

  const languageOptions = useMemo(() => {
    const names = new Intl.DisplayNames([locale], { type: 'language' });
    return LANGUAGE_CODES.map((code) => ({ code, label: names.of(code) ?? code })).sort((a, b) =>
      a.label.localeCompare(b.label, locale),
    );
  }, [locale]);

  const saved = useMemo(() => (restaurant ? profileValues(restaurant) : undefined), [restaurant]);
  const form = useContractForm(profileSchema(tr.contactPhoneInvalid), {
    defaultValues: EMPTY_PROFILE,
    values: saved,
    toBody: (values) => profileBody(values, saved ?? EMPTY_PROFILE, restaurant),
  });
  const nameInput = useListInput(form.control, 'name');
  const addressInput = useListInput(form.control, 'address');
  const postalCodeInput = useListInput(form.control, 'postalCode');
  const cityInput = useListInput(form.control, 'city');
  const contactEmailInput = useListInput(form.control, 'contactEmail');
  const contactPhoneInput = useListInput(form.control, 'contactPhone');
  const facebookInput = useListInput(form.control, 'facebookUrl');
  const instagramInput = useListInput(form.control, 'instagramUrl');
  const tiktokInput = useListInput(form.control, 'tiktokUrl');
  const websiteInput = useListInput(form.control, 'websiteUrl');
  const { field: countryField } = useController({
    control: form.control,
    name: 'country',
    rules: { deps: 'contactPhone' },
  });

  // Language picker popover — same tap-a-row UX as the order-status popover
  // (OrderActions): a single ListItem opens an anchored Popover of choices.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const languagePopoverRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const countryPopoverRef = useRef<any>(null);
  const openCountryPopover = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      if (countryPopoverRef.current?.el) f7.popover.open(countryPopoverRef.current.el, target);
    });
  };
  const centerSelectedCountry = (popoverEl: HTMLElement | undefined) => {
    const list = popoverEl?.querySelector<HTMLElement>('.list');
    const selected = list?.querySelector<HTMLElement>('.country-picker-selected');
    if (!list || !selected) return;
    const offset = selected.getBoundingClientRect().top - list.getBoundingClientRect().top;
    list.scrollTop += offset - (list.clientHeight - selected.offsetHeight) / 2;
  };
  const openLanguagePopover = (e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      if (languagePopoverRef.current?.el) f7.popover.open(languagePopoverRef.current.el, target);
    });
  };

  const restaurantDirty =
    form.formState.isDirty &&
    Object.keys(profileBody(form.watch(), saved ?? EMPTY_PROFILE, restaurant)).length > 0;
  const isDirty = restaurantDirty || languageDirty;
  const saving = update.isPending || updateFranchise.isPending;
  const saveBlocked = Object.keys(form.formState.errors).length > 0;

  const handleSave = form.handleSubmit(async (body) => {
    const sent = form.getValues();
    try {
      // Restaurant fields and the franchise-level language are two resources -
      // touch each only when it actually changed (avoids a redundant write and,
      // for language, a franchise call the caller may not be authorized for).
      const ops: Promise<unknown>[] = [];
      if (restaurantDirty) {
        ops.push(
          update.mutateAsync(body).then((next) => {
            const typed = form.getValues();
            form.reset(profileValues(next), { keepDirtyValues: false });
            for (const field of PROFILE_FIELDS) {
              if (typed[field] !== sent[field]) {
                form.setValue(field, typed[field], { shouldDirty: true, shouldValidate: true });
              }
            }
          }),
        );
      }
      if (languageDirty) {
        ops.push(updateFranchise.mutateAsync({ defaultLanguage: language }));
      }
      await Promise.all(ops);
      f7.toast.show({ text: t.saved, closeTimeout: 2000, position: 'bottom' });
    } catch (err) {
      // Render the localized text for the failure - crucially, a bare 403 from
      // the franchise/restaurant update (e.g. staff without owner permission
      // changing the venue language) surfaces the "no permission" message rather
      // than a raw "Forbidden" / status code. Falls back to saveFailed for a
      // non-ApiError (e.g. a network throw with no useful text).
      const text = err instanceof ApiError ? resolveErrorText(err, errorMessages) : tr.saveFailed;
      f7.toast.show({ text, closeTimeout: 2500, position: 'bottom' });
    }
  });

  return (
    <Page name="restaurant-setup">
      <Navbar title={t.settings.restaurantSetup} backLink={settingsBackLink}>
        <SettingsSaveNav
          isDirty={isDirty}
          saving={saving}
          disabled={saveBlocked}
          onSave={handleSave}
        />
      </Navbar>

      {isLoading ? (
        <Block>
          <p className="text-gray-500">{t.loading}</p>
        </Block>
      ) : (
        <>
          <BlockTitle>{tr.basicInfoSection}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={tr.name}
              type="text"
              {...nameInput}
              placeholder={tr.namePlaceholder}
              clearButton
            />
            <ListInput
              label={tr.address}
              type="text"
              {...addressInput}
              placeholder={tr.addressPlaceholder}
              clearButton
            />
            <ListInput
              label={tr.postalCode}
              type="text"
              {...postalCodeInput}
              placeholder={tr.postalCodePlaceholder}
              clearButton
            />
            <ListInput
              label={tr.city}
              type="text"
              {...cityInput}
              placeholder={tr.cityPlaceholder}
              clearButton
            />
            <ListItem
              title={tr.country}
              after={countryOptions.find((c) => c.code === countryField.value)?.label}
              link="#"
              onClick={openCountryPopover}
            />
            <ListInput
              label={tr.contactEmail}
              type="email"
              {...contactEmailInput}
              placeholder={tr.contactEmailPlaceholder}
              clearButton
            />
            <ListInput
              label={tr.contactPhone}
              type="tel"
              {...contactPhoneInput}
              placeholder={tr.contactPhonePlaceholder}
              clearButton
            />
          </List>
          <Popover
            ref={countryPopoverRef}
            onPopoverOpen={(popover) => centerSelectedCountry(popover?.el)}
          >
            <List
              strongIos
              dividersIos
              style={{ margin: 0, maxHeight: 360, overflowY: 'auto', width: 260 }}
            >
              {countryOptions.map((c) => (
                <ListItem
                  key={c.code}
                  className={c.code === countryField.value ? 'country-picker-selected' : undefined}
                  title={c.label}
                  link="#"
                  noChevron
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    followCountryChange(c.code);
                    countryField.onChange(c.code);
                    if (countryPopoverRef.current?.el)
                      f7.popover.close(countryPopoverRef.current.el);
                  }}
                >
                  {c.code === countryField.value && (
                    <i
                      slot="after"
                      className="f7-icons"
                      style={{ color: 'var(--f7-theme-color)', fontSize: 22 }}
                    >
                      checkmark
                    </i>
                  )}
                </ListItem>
              ))}
            </List>
          </Popover>

          {/* Public web/social presence. Shown to guests as a link row on the
              storefront info card, so it sits with the other public contact
              details rather than under branding. A bare handle is accepted —
              the backend expands it to the platform URL and rejects a link
              pasted into the wrong platform's field. */}
          <BlockTitle>{tr.socialSection}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={tr.facebookUrl}
              type="text"
              inputmode="url"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              {...facebookInput}
              placeholder={tr.facebookUrlPlaceholder}
              clearButton
            >
              <span slot="media">
                <SocialPlatformIcon field="facebookUrl" />
              </span>
            </ListInput>
            <ListInput
              label={tr.instagramUrl}
              type="text"
              inputmode="url"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              {...instagramInput}
              placeholder={tr.instagramUrlPlaceholder}
              clearButton
            >
              <span slot="media">
                <SocialPlatformIcon field="instagramUrl" />
              </span>
            </ListInput>
            <ListInput
              label={tr.tiktokUrl}
              type="text"
              inputmode="url"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              {...tiktokInput}
              placeholder={tr.tiktokUrlPlaceholder}
              clearButton
            >
              <span slot="media">
                <SocialPlatformIcon field="tiktokUrl" />
              </span>
            </ListInput>
            <ListInput
              label={tr.websiteUrl}
              type="text"
              inputmode="url"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              {...websiteInput}
              placeholder={tr.websiteUrlPlaceholder}
              clearButton
            >
              <span slot="media">
                <SocialPlatformIcon field="websiteUrl" />
              </span>
            </ListInput>
          </List>
          <Block className="text-gray-500" style={{ marginTop: -8 }}>
            <p style={{ fontSize: 13 }}>{tr.socialFooter}</p>
          </Block>

          {/* Venue language — the franchise `defaultLanguage`. Drives printed
              ticket labels and the dashboard/storefront/notification default.
              Franchise-scoped (owner), so it sits in its own section, not mixed
              into the restaurant address fields. */}
          <BlockTitle>{tr.venueLanguageSection}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              link="#"
              title={tr.venueLanguage}
              after={languageOptions.find((l) => l.code === language)?.label}
              onClick={openLanguagePopover}
            />
          </List>
          <Popover ref={languagePopoverRef} closeByOutsideClick backdrop style={{ minWidth: 220 }}>
            <List noChevron style={{ minWidth: 220, margin: 0 }}>
              {languageOptions.map((l) => (
                <ListItem
                  key={l.code}
                  link="#"
                  title={l.label}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    pickLanguage(l.code);
                    f7.popover.close();
                  }}
                >
                  {l.code === language && (
                    <i slot="after" className="f7-icons" style={{ color: '#248a3d', fontSize: 22 }}>
                      checkmark_alt
                    </i>
                  )}
                </ListItem>
              ))}
            </List>
          </Popover>
          <Block className="text-gray-500" style={{ marginTop: -8 }}>
            <p style={{ fontSize: 13 }}>{tr.venueLanguageFooter}</p>
          </Block>

          {/* Map pin — the origin distance-based delivery pricing measures from.
              Lives with the business address (not in Online Order settings) since
              it's a property of the place, not of the ordering channel. Opens the
              full-screen map editor as a slide-in page (Rule 12). */}
          <BlockTitle>{lp.restaurantLocation}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              link="#"
              onClick={() => f7.views.current.router.navigate('/settings/restaurant/location')}
              title={lp.restaurantLocation}
              after={hasLocation ? lp.pinned : lp.notSet}
              footer={lp.locationFooter}
            >
              <i slot="media" className="f7-icons">
                placemark
              </i>
            </ListItem>
          </List>
        </>
      )}
    </Page>
  );
}

export default RestaurantSetupPage;
